import { useEffect, useId, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { getFileUrl, useFileUpload } from '@/shared/hooks/useFileUpload';
import { cn } from '@/shared/utils/cn';

type InlineFileUploadProps = {
  label: string;
  value?: string;
  onChange: (url: string) => void;
  accept?: string;
  maxSizeMB?: number;
  hint?: string;
  error?: string;
  previewClassName?: string;
  disabled?: boolean;
  /** Lets a parent form block Save while a file is still in flight. */
  onUploadingChange?: (uploading: boolean) => void;
};

export function InlineFileUpload({
  label,
  value,
  onChange,
  accept = 'image/*',
  maxSizeMB = 5,
  hint,
  error,
  previewClassName = 'h-32',
  disabled = false,
  onUploadingChange,
}: InlineFileUploadProps) {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [uploadProgress, setUploadProgress] = useState(0);

  const { uploadAsync, isUploading } = useFileUpload({
    maxSizeMB,
    onProgress: (progress) => setUploadProgress(progress),
  });

  const hydrateImageUrl = (fileKey?: string): string | undefined => {
    if (!fileKey || fileKey.trim() === '') {
      return undefined;
    }

    if (fileKey.startsWith('http://') || fileKey.startsWith('https://') || fileKey.startsWith('blob:')) {
      return fileKey;
    }

    return getFileUrl(fileKey);
  };

  const [previewUrl, setPreviewUrl] = useState<string | undefined>(() => hydrateImageUrl(value));
  const isDisabled = disabled || isUploading;
  const messageId = `${inputId}-message`;

  useEffect(() => {
    setPreviewUrl(hydrateImageUrl(value));
  }, [value]);

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setUploadError(undefined);
    setUploadProgress(0);

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setUploadError(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    if (accept && !file.type.match(accept.replace('*', '.*'))) {
      setUploadError('Invalid file type');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    try {
      const fileKey = await uploadAsync(file);
      onChange(fileKey);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      console.error('Upload error:', err);
      setPreviewUrl(undefined);
    }
  };

  const handleClear = () => {
    setPreviewUrl(undefined);
    setUploadError(undefined);
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-3">
      <label htmlFor={inputId} className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex h-32 w-32 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-gray-300 bg-gray-50',
            previewClassName,
          )}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Preview" className="h-full w-full object-cover" />
          ) : (
            <ImageIcon className="h-8 w-8 text-gray-400" />
          )}
        </div>

        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleClick} disabled={isDisabled}>
              <Upload className="mr-2 h-4 w-4" />
              {isUploading ? 'Uploading...' : previewUrl ? 'Change' : 'Upload'}
            </Button>

            {previewUrl && (
              <Button type="button" variant="outline" size="sm" onClick={handleClear} disabled={isDisabled}>
                <X className="mr-2 h-4 w-4" />
                Remove
              </Button>
            )}
          </div>

          {isUploading && (
            <div className="space-y-1">
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-600">Uploading... {uploadProgress}%</p>
            </div>
          )}

          {!isUploading && hint && !uploadError && !error && (
            <p id={messageId} className="text-xs text-gray-500">
              {hint}
            </p>
          )}
          {(uploadError || error) && (
            <p id={messageId} className="text-sm text-red-600">
              {uploadError || error}
            </p>
          )}
        </div>
      </div>

      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        disabled={isDisabled}
        aria-invalid={Boolean(uploadError || error)}
        aria-describedby={hint || uploadError || error ? messageId : undefined}
        className="hidden"
      />
    </div>
  );
}
